import { observer } from "mobx-react";
import PolicyStore from "../../../../../stores/policy-store";
import { FormFieldLine } from "../../../../../../../components/form-field-line/form-field-line";
import { Toggle } from "../../../../../../../components/inputs/toggle/toggle";
import { Note } from "../../../../../../../components/note/note";
import FontBody2 from "../../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../../components/fonts/font-props";
import ControlGroup from "../../../../../../../components/layouts/control-group/control-group";
import { Input } from "../../../../../../../components/inputs/input/input";
import { IOption, SelectDropdown } from "../../../../../../../components/inputs/select-dropdown/select-dropdown";
import { SingleValue } from "react-select";
import config from "../../../../../../../config";
import AHref from "../../../../../../../components/ahref/ahref";
import "./reserve-items.scss";
import Hr from "../../../../../../../components/hr/hr";
import currentPortalStore from "../../../../../../../stores/current-portal-store";
import { FEATURES } from "../../../../../../../models/Plan";

interface IProps {
    policy: PolicyStore;
}

const ReserveItems: React.FC<IProps> = observer(({ policy }) => {
    const isReserveItemsDisabled = policy.isReserveItemsDisabled;

    const restockTime = policy?.restockTime ? {
        label: policy.restockTime,
        value: policy.restockTime
    } : undefined;
    const onChangeRestockTime = (value: SingleValue<IOption>) => {
        policy?.setRestockTime(value?.value as string);
    }
    const returnMethodsLink = `${config.apiUrl}/dashboard/settings/return-methods`;
    return (
        <>
            <Hr />
            <FormFieldLine
                label='Reserve Items'
                note='Reserve items when processing an exchange.'
            >{null}</FormFieldLine>

            <FormFieldLine
                label='Reserve Items for Exchanges'
                note='Enable to reserve exchanged items for a set amount of time to guarantee inventory availability for exchanges.'
                disabled={isReserveItemsDisabled}
                tooltip={!currentPortalStore.isFeatureAllowed(FEATURES.RESERVE_ITEMS) && <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>To enable this feature, go to the Manage Add-ons section.</FontBody2>}
            >
                <Toggle
                    onChange={policy.setEnableReserveItems}
                    disabled={isReserveItemsDisabled}
                    label={""}
                    checked={policy.enableReserveItems}
                />
            </FormFieldLine>
            {(policy.enableReserveItems && !isReserveItemsDisabled) && (<>
                <Note body={<>
                    Make sure to inform your customers about the timeframe for reserving exchanged items in the shipping instructions of this policy rule's <AHref href={returnMethodsLink} target="_parent">
                        <FontBody2 color={FONT_COLOR.ACCENT1}>Return Method</FontBody2>
                    </AHref>
                </>} />
                <FormFieldLine
                    label='Item Reservation Timeframe'
                    note='Set the item reservation timeframe based on the amount of time it typically takes you to approve an exchange request.'
                    tooltip={
                        <div className="item-reservation-timeframe-tooltip">
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                When setting the number of days items will be reserved, take into account:
                                <ul>
                                    <li>The average amount of time it takes to approve pending RMAs.</li>
                                    <li>The average number of days you will wait for customers to ship items back.</li>
                                    <li>The average amount of time it takes you to handle and inspect received shipments.</li>
                                    <li>The average amount of time it takes to release the exchange once a shipment is inspected.</li>
                                </ul>
                            </FontBody2>
                        </div>
                    }
                >
                    <ControlGroup>
                        <div className="reserve-item-input-container">
                            <Input
	                            required={policy.enableReserveItems}
                                value={policy?.itemsReservationTimeframe ?? undefined}
                                onChange={(v) => policy.setItemReservationTimeframe(v)}
                                maxlength={6}
                                min={0}
                                type="number"
                            />
                            <FontBody2 color={FONT_COLOR.PRIMARY3_50} variant={FONT_VARIANT.BOLD}>
                                Days
                            </FontBody2>
                        </div>
                    </ControlGroup>

                </FormFieldLine>
                <FormFieldLine
                    label='Restock Time'
                    note='The time at which reserved items will be returned to the inventory when the item reservation timeframe ends.'
                >
                    <div className="reserve-item-input-container">
                        <SelectDropdown
	                        required={policy.enableReserveItems}
                            options={policy.restockTimeOptions}
                            value={restockTime}
                            onChange={onChangeRestockTime}
                            isSearchable={false}
                        />
                        <FontBody2 color={FONT_COLOR.PRIMARY3_50} variant={FONT_VARIANT.BOLD}>UTC</FontBody2>
                    </div>
                </FormFieldLine>
            </>)}
        </>
    );
});

export default ReserveItems;